(() => {
  const start = () => {
    if (document.documentElement.dataset.adamsReady) return;
    const configElement = document.getElementById('at-config');
    if (!configElement || !window.AdamsDraftMath) return;
    document.documentElement.dataset.adamsReady = 'true';
    const config = JSON.parse(configElement.textContent);
    const math = window.AdamsDraftMath;
    const products = config.products;
    const text = config.text;
    const money = cents => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(cents/100);
    const esc = value => String(value).replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    let state = math.normalize({});
    let cart = null;
    let busy = false;
    let cartReady = false;
    let quantityTimer = null;
    let pendingQuantity = null;
    const variantMap = new Map(products.timeline.variantIds.map((id,i)=>[id,{key:'timeline',units:i+1}]));
    variantMap.set(products.stand.variantId,{key:'stand',units:1});
    variantMap.set(products.magnifier.variantId,{key:'magnifier',units:1});
    const cartRoot = (config.cartRoot || '/').replace(/\/?$/, '/');
    const showError = message => {
      const node = document.querySelector('[data-checkout-error]');
      node.textContent = message || '';
      node.hidden = !message;
    };
    const setBusy = value => {
      busy = value;
      document.querySelectorAll('[data-add-main],[data-sticky-add],[data-add],[data-change],[data-quantity],[data-remove],[data-upgrade],[data-checkout]').forEach(node=>node.disabled=value);
      document.getElementById('at-cart').setAttribute('aria-busy',String(value));
      document.querySelector('[data-cart-status]').textContent=value?text.updatingCart:'';
    };
    const cartRequest = async (endpoint,body) => {
      const controller = new AbortController();
      const timeout = setTimeout(()=>controller.abort(),12000);
      try {
        const url = cartRoot + endpoint + (body ? '' : '?_=' + Date.now());
        const response = await fetch(url,{
          method:body?'POST':'GET', cache:'no-store', signal:controller.signal, credentials:'same-origin',
          headers:{Accept:'application/json',...(body?{'Content-Type':'application/json'}:{})},
          ...(body?{body:JSON.stringify(body)}:{})
        });
        const data = await response.json();
        if(!response.ok || data.status>=400)throw new Error(data.description || data.message || text.cartError);
        return data;
      } catch(error) {
        if (error.name==='AbortError') throw new Error('The cart request timed out. Please check your cart and try again.');
        throw error;
      } finally { clearTimeout(timeout); }
    };
    // Adams and ViseHound previews share Shopify's native cart. Remove only
    // the two explicitly excluded ViseHound products; never hide charged items.
    const readAdamsCart = async () => {
      const fresh=await cartRequest('cart.js');
      const excluded=new Set(config.excludedProductIds || []);
      const updates=Object.fromEntries(fresh.items.filter(item=>excluded.has(item.product_id)).map(item=>[item.key,0]));
      return Object.keys(updates).length ? cartRequest('cart/update.js',{updates}) : fresh;
    };
    const acceptCart = data => {
      cart=data;
      state=math.normalize({});
      data.items.forEach(item=>{
        const mapped=variantMap.get(item.variant_id);
        if(mapped)state[mapped.key]+=item.quantity*mapped.units;
      });
      cartReady=true;
      renderCart();
    };
    const changeCart = async (key,quantity,relative=false) => {
      if(busy || !products[key])return false;
      setBusy(true);showError('');
      try{
        const fresh = await readAdamsCart();
        const current=fresh.items.reduce((sum,item)=>{
          const mapped=variantMap.get(item.variant_id);
          return sum+(mapped?.key===key?item.quantity*mapped.units:0);
        },0);
        const desired=math.cleanQuantity(relative ? current+quantity : quantity);
        const ids=products.timeline.variantIds;
        const targets = key==='timeline' ? [[ids[0],desired%3===1?1:0],[ids[1],desired%3===2?1:0],[ids[2],Math.floor(desired/3)]] : [[products[key].variantId,desired]];
        const updates={};
        for(const [id,quantity] of targets){
          const matches=fresh.items.filter(item=>item.variant_id===id);
          if(matches.length)matches.forEach((item,index)=>{
            const target=index===0?quantity:0;
            if(item.quantity!==target)updates[item.key]=target;
          });
          else if(quantity)updates[id]=quantity;
        }
        acceptCart(Object.keys(updates).length ? await cartRequest('cart/update.js',{updates}) : fresh);
        return true;
      }catch(error){
        showError(error.message || text.cartError);
        try{acceptCart(await readAdamsCart());}catch(_){cartReady=false;}
        return false;
      }finally{setBusy(false);}
    };
    const commitQuantity = async () => {
      clearTimeout(quantityTimer);
      if(!pendingQuantity)return true;
      const change=pendingQuantity;
      pendingQuantity=null;
      return changeCart(change.key,change.quantity);
    };
    const checkout = async () => {
      if(pendingQuantity && !await commitQuantity())return;
      if(busy)return;
      if(!cartReady)await refreshCart();
      if(!cartReady || !cart.items.length)return;
      // Hand the confirmed cart to Shopify. A second AJAX read must not replace
      // a successful mutation with a cached empty response before navigation.
      location.assign(cartRoot+'checkout');
    };
    let returnFocus = null;
    const route = view => view ? '/?view=' + encodeURIComponent(view) : '/';
    const openDialog = id => {
      const dialog = document.getElementById(id);
      if (!dialog || dialog.open) return;
      if (!document.querySelector('dialog[open]')) returnFocus = document.activeElement;
      document.querySelectorAll('dialog[open]').forEach(item=>item.close());
      dialog.showModal();
    };
    const closeDialog = dialog => {
      if (!dialog) return;
      dialog.close();
      if (returnFocus && returnFocus.isConnected) returnFocus.focus({preventScroll:true});
    };
    document.querySelectorAll('dialog').forEach(dialog=>{
      dialog.addEventListener('click',event=>{
        if (event.target !== dialog) return;
        const box=dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog(dialog);
      });
      dialog.addEventListener('cancel',()=>{ if(returnFocus && returnFocus.isConnected) returnFocus.focus({preventScroll:true}); });
    });

    const lineHTML = line => {
      const p = products[line.key];
      const saved = line.original - line.total;
      const upgrade = line.key === 'timeline' && line.quantity < 3 ? '<button class="at-upgrade" data-upgrade>Add 1 more to get ' + money((line.quantity+1)*5995-math.timelineTotal(line.quantity+1)) + ' off</button>' : '';
      return '<article class="at-cart-line" data-line="' + line.key + '"><a href="' + route(p.view) + '"><img src="' + esc(p.image) + '&width=180" width="82" height="82" alt="' + esc(p.title) + '"></a><div><a class="at-line-title" href="' + route(p.view) + '">' + (line.quantity>1 ? line.quantity+'x ' : '') + esc(p.title) + '</a>' + (line.key==='timeline' ? '<span class="at-line-tag">Buy '+line.quantity+'</span>' : '') + '<div class="at-quantity"><button data-change="' + line.key + '" data-delta="-1" aria-label="' + esc(text.decrease+' — '+p.title) + '">−</button><input data-quantity="' + line.key + '" type="number" min="0" max="99" value="' + line.quantity + '" aria-label="' + esc(text.quantity+' — '+p.title) + '"><button data-change="' + line.key + '" data-delta="1" aria-label="' + esc(text.increase+' — '+p.title) + '">+</button></div>'+upgrade+'</div><div class="at-line-prices">' + (saved>0?'<s>'+money(line.original)+'</s>':'') + '<strong>'+money(line.total)+'</strong>' + (saved>0?'<small class="at-saving">'+esc(text.saving)+' '+money(saved)+'</small>':'') + '<button class="at-remove" data-remove="'+line.key+'" aria-label="'+esc(text.remove+' '+p.title)+'"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7"/></svg></button></div></article>';
    };
    const merchandiseSubtotal = cartData => {
      // Never let a delivery estimate retained after checkout count as merchandise.
      // final_line_price already includes line-level (including accessory) discounts.
      const lineSubtotal=cartData.items.reduce((sum,item)=>sum+item.final_line_price,0);
      const cartDiscount=(cartData.cart_level_discount_applications || [])
        .filter(discount=>discount.target_type !== 'shipping_line')
        .reduce((sum,discount)=>sum+discount.total_allocated_amount,0);
      return Math.max(0,lineSubtotal-cartDiscount);
    };
    const renderCart = () => {
      const result=math.calculate(state);
      const extraItems=cart?cart.items.filter(item=>!variantMap.has(item.variant_id)):[];
      if(cart){
        result.lines.forEach(line=>{line.total=cart.items.filter(item=>variantMap.get(item.variant_id)?.key===line.key).reduce((total,item)=>total+item.final_line_price,0);});
        result.total=merchandiseSubtotal(cart);
        result.savings=result.lines.reduce((sum,line)=>sum+Math.max(0,line.original-line.total),0);
        result.count+=extraItems.reduce((sum,item)=>sum+item.quantity,0);
      }
      document.querySelectorAll('[data-cart-count]').forEach(node=>{node.textContent=result.count;node.hidden=result.count===0 && node.closest('.at-bag');});
      document.querySelector('[data-cart-lines]').innerHTML=result.lines.map(lineHTML).join('')+extraItems.map(item=>'<article class="at-cart-line at-cart-other"><div><a class="at-line-title" href="'+esc(item.url)+'">'+esc(item.product_title)+'</a><p>'+esc(text.otherCartItem)+' · '+item.quantity+'</p></div><strong>'+money(item.final_line_price)+'</strong></article>').join('');
      document.querySelector('[data-cart-empty]').hidden=!cartReady || result.count>0;
      document.querySelector('[data-cart-footer]').hidden=result.count===0;
      document.querySelector('[data-cart-progress]').hidden=result.count===0;
      document.querySelector('[data-cart-progress-fill]').style.width=Math.min(100,result.total/5000*100)+'%';
      document.querySelector('[data-cart-progress-label]').textContent=result.total>=5000 ? text.shippingUnlocked : text.shippingRemaining.replace('[amount]',money(5000-result.total));
      document.querySelector('[data-upsells]').hidden=!state.timeline || (state.stand>0 && state.magnifier>0);
      document.querySelectorAll('[data-upsell]').forEach(node=>node.hidden=state[node.dataset.upsell]>0);
      document.querySelectorAll('[data-cart-subtotal]').forEach(node=>node.textContent=money(result.total));
      document.querySelector('[data-cart-savings]').textContent='−'+money(result.savings);
      document.querySelector('[data-cart-offer]').textContent=state.timeline ? 'Buy '+state.timeline : '';
      setBusy(busy);
    };
    const add = async (key,quantity=1) => {
      if(!products[key])return;
      if(pendingQuantity && !await commitQuantity())return;
      openDialog('at-cart');
      await changeCart(key,quantity,true);
    };
    const ticker=document.querySelector('.at-ticker');
    if(ticker){
      const toggleTicker=()=>ticker.classList.toggle('is-paused');
      ticker.addEventListener('click',toggleTicker);
      ticker.addEventListener('keydown',event=>{if(event.key===' '||event.key==='Enter'){event.preventDefault();toggleTicker();}});
    }
    let galleryIndex=0;
    let swipeTime=0;
    const gallery = document.querySelector('.at-gallery');
    const thumbs = [...document.querySelectorAll('[data-gallery-image]')];
    const product = document.querySelector('[data-product]');
    const currentKey=product ? product.dataset.product : 'timeline';
    const mainImage = document.querySelector('.at-zoom-trigger img');
    const setImage=index=>{
      if (!thumbs.length || !mainImage)return;
      galleryIndex=(index+thumbs.length)%thumbs.length;
      const url=thumbs[galleryIndex].dataset.galleryImage;
      mainImage.src=url+'&width=1200';
      mainImage.srcset=[360,600,1000,1500].map(width=>url+'&width='+width+' '+width+'w').join(', ');
      mainImage.alt=products[currentKey].title+' — view '+(galleryIndex+1);
      thumbs.forEach((button,i)=>button.setAttribute('aria-pressed',String(i===galleryIndex)));
      thumbs[galleryIndex].scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'nearest',inline:'nearest'});
      document.querySelector('[data-zoom-image]').src=url+'&width=1800';
      document.querySelector('[data-zoom-image]').alt=mainImage.alt;
    };
    if(gallery){
      let startX=0,startY=0;
      gallery.addEventListener('touchstart',event=>{startX=event.changedTouches[0].clientX;startY=event.changedTouches[0].clientY;},{passive:true});
      gallery.addEventListener('touchend',event=>{
        const dx=event.changedTouches[0].clientX-startX,dy=event.changedTouches[0].clientY-startY;
        if(Math.abs(dx)>45 && Math.abs(dx)>Math.abs(dy)*1.3){swipeTime=Date.now();setImage(galleryIndex+(dx<0?1:-1));}
      },{passive:true});
      gallery.addEventListener('keydown',event=>{if(event.key==='ArrowRight'||event.key==='ArrowLeft'){event.preventDefault();setImage(galleryIndex+(event.key==='ArrowRight'?1:-1));}});
    }
    let reviewIndex=0;
    const quotes=[...document.querySelectorAll('[data-quote]')];
    const setReview=index=>{
      if(!quotes.length)return;
      reviewIndex=(index+quotes.length)%quotes.length;
      quotes.forEach((q,i)=>q.hidden=i!==reviewIndex);
      document.querySelectorAll('[data-review-index]').forEach((b,i)=>b.setAttribute('aria-pressed',String(i===reviewIndex)));
    };
    const selectedSet=()=>Number(document.querySelector('.at-bundles input:checked')?.value||1);
    const addMain=()=>add(currentKey,currentKey==='timeline'?selectedSet():math.cleanQuantity(document.querySelector('[data-accessory-quantity]')?.value||1)||1);
    const updateSticky=()=>{const price=document.querySelector('[data-sticky-price]');if(price)price.textContent=money(products.timeline.price);};
    const searchInput=document.querySelector('[data-search-input]');
    const searchResults=document.querySelector('[data-search-results]');
    const renderSearch=()=>{
      const query=searchInput.value.toLowerCase().trim();
      const matches=Object.values(products).filter(p=>p.title.toLowerCase().includes(query));
      searchResults.innerHTML=matches.length?matches.map(p=>'<a class="at-search-result" href="'+route(p.view)+'"><img src="'+esc(p.image)+'&width=160" alt="" width="64" height="64"><span>'+esc(p.title)+'<small>'+money(p.price)+'</small></span></a>').join(''):'<p>'+esc(text.noResults)+'</p>';
    };
    searchInput.addEventListener('input',renderSearch);
    renderSearch();
    document.addEventListener('click',async event=>{
      const button=event.target.closest('button');
      if(!button)return;
      if(button.hasAttribute('data-close')){closeDialog(button.closest('dialog'));return;}
      if(button.dataset.open){openDialog(button.dataset.open);if(button.dataset.open==='at-cart')await refreshCart();return;}
      if(button.dataset.info){
        const key=button.dataset.info;
        document.getElementById('at-info-title').textContent=text.infoTitles[key]||key;
        document.querySelector('[data-info-copy]').textContent=text.infoCopy[key]||text.infoCopy.policy;
        openDialog('at-info');return;
      }
      if(button.hasAttribute('data-add-main')){addMain();return;}
      if(button.hasAttribute('data-sticky-add')){add('timeline',1);return;}
      if(button.dataset.add){add(button.dataset.add);return;}
      if(button.dataset.change){
        const key=button.dataset.change,delta=Number(button.dataset.delta);
        if(pendingQuantity && !await commitQuantity())return;
        await changeCart(key,delta,true);
        document.querySelector('[data-change="'+key+'"][data-delta="'+delta+'"]')?.focus({preventScroll:true});
        return;
      }
      if(button.dataset.remove){
        if(pendingQuantity?.key===button.dataset.remove){clearTimeout(quantityTimer);pendingQuantity=null;}
        if(pendingQuantity && !await commitQuantity())return;
        await changeCart(button.dataset.remove,0);return;
      }
      if(button.hasAttribute('data-upgrade')){if(pendingQuantity && !await commitQuantity())return;await changeCart('timeline',1,true);return;}
      if(button.hasAttribute('data-checkout')){checkout(button);return;}
      if(button.hasAttribute('data-back-cart')){openDialog('at-cart');return;}
      if(button.hasAttribute('data-gallery-image')){setImage(thumbs.indexOf(button));return;}
      if(button.hasAttribute('data-gallery-prev')||button.hasAttribute('data-zoom-prev')){setImage(galleryIndex-1);return;}
      if(button.hasAttribute('data-gallery-next')||button.hasAttribute('data-zoom-next')){setImage(galleryIndex+1);return;}
      if(button.hasAttribute('data-zoom')){if(Date.now()-swipeTime<400)return;setImage(galleryIndex);openDialog('at-zoom');return;}
      if(button.hasAttribute('data-review-index')){setReview(Number(button.dataset.reviewIndex));return;}
      if(button.hasAttribute('data-review-prev')){setReview(reviewIndex-1);return;}
      if(button.hasAttribute('data-review-next'))setReview(reviewIndex+1);
    });
    document.addEventListener('input',event=>{
      if(!event.target.matches('[data-quantity]'))return;
      clearTimeout(quantityTimer);
      const input=event.target;
      pendingQuantity=input.value!=='' && input.validity.valid ? {key:input.dataset.quantity,quantity:Number(input.value)} : null;
    });
    document.addEventListener('change',event=>{
      if(event.target.matches('.at-bundles input'))updateSticky();
    });
    document.addEventListener('focusout',event=>{
      if(!event.target.matches('[data-quantity]'))return;
      if(!pendingQuantity){event.target.value=state[event.target.dataset.quantity];return;}
      clearTimeout(quantityTimer);
      quantityTimer=setTimeout(commitQuantity,300);
    });
    document.addEventListener('keydown',event=>{
      if(event.key==='Enter' && event.target.matches('[data-quantity]')){event.preventDefault();commitQuantity();return;}
      if(document.getElementById('at-zoom').open && (event.key==='ArrowRight'||event.key==='ArrowLeft')){event.preventDefault();setImage(galleryIndex+(event.key==='ArrowRight'?1:-1));}
    });
    const addButton=document.querySelector('[data-add-main]');
    if(addButton && currentKey==='timeline'){
      const sticky=document.querySelector('.at-sticky-add');
      new IntersectionObserver(entries=>{sticky.hidden=entries[0].isIntersecting||entries[0].boundingClientRect.top>0;},{threshold:0}).observe(addButton);
    }
    updateSticky();
    renderCart();
    async function refreshCart(){
      if(busy)return;
      setBusy(true);
      try{acceptCart(await readAdamsCart());showError('');}
      catch(error){showError(error.message || text.cartError);}
      finally{setBusy(false);}
    }
    refreshCart();
    window.addEventListener('pageshow',event=>{if(event.persisted)refreshCart();});
    if(new URLSearchParams(location.search).get('view')==='at-cart' || location.pathname.replace(/\/$/,'')===cartRoot+'cart')openDialog('at-cart');
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start);else start();
})();
